import React, { useState, useRef } from 'react';
import { useTransition } from 'react-spring';

import useOutsideClick from 'hooks/useOutsideClick';

import { FloatingWrapper, FloatingToggleButton, FloatingAction, FloatingToggleButtonIcon } from './styles';

type ItemProps = {
  label: string;
  icon: string;
  onClick?(event: React.MouseEvent<HTMLButtonElement>): void;
};

type Props = {
  icon: string;
  label: string;
  items: ItemProps[];
  defaultOpen?: boolean;
};

const FloatingMenu = ({ icon, label, items, defaultOpen }: Props) => {
  const [isOpen, setIsOpen] = useState(defaultOpen || false);
  const menuRef = useRef<HTMLDivElement>(null);

  useOutsideClick(menuRef, () => {
    setIsOpen(false);
  });

  const transitionToggleIcon = useTransition(isOpen, {
    // TODO: remove `as any` when this is resolved:
    // https://github.com/react-spring/react-spring/issues/1102
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    from: { opacity: 0 as any, transform: 'rotate(180deg)' },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    enter: { opacity: 1 as any, transform: 'rotate(0deg)' },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    leave: { opacity: 0 as any, transform: 'rotate(0deg)' },
    keys: (e) => e,
  });

  const transitionItems = useTransition(isOpen ? items : [], {
    // TODO: remove `as any` when this is resolved:
    // https://github.com/react-spring/react-spring/issues/1102
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    from: (_item, i) => ({ opacity: 0 as any, transform: `translateY(${(items.length - i) * 48}px)` }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    enter: { opacity: 1 as any, transform: 'translateY(0px)' },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    leave: (_item, i) => ({ opacity: 0 as any, transform: `translateY(${(items.length - i) * 64}px)` }),
    keys: (e) => e.label,
    unique: true,
  });

  return (
    <FloatingWrapper ref={menuRef}>
      {transitionItems((style, item) => (
        <FloatingAction onClick={item.onClick} style={style} key={item.label}>
          <FloatingToggleButtonIcon src={`/images/${item.icon}.svg`} alt={item.label} />
        </FloatingAction>
      ))}

      <FloatingToggleButton
        $isOpen={isOpen}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        tabIndex={0}
      >
        {transitionToggleIcon((style, item) =>
          item ? (
            <FloatingToggleButtonIcon style={style} src="/images/close-2.svg" />
          ) : (
            <FloatingToggleButtonIcon style={style} src={`/images/${icon}.svg`} alt={label} />
          ),
        )}
      </FloatingToggleButton>
    </FloatingWrapper>
  );
};

export default FloatingMenu;
